((scope) => {
  const createModel = modules => {
    const definitions = new Map(modules.map(item => [item.id, item]));
    const colours = ['bone','grey'];
    const moduleFor = (id,colour='bone') => {const base=definitions.get(id);return base ? {...base,...base.colours?.[colour]} : undefined;};
    const joinDepth = definitions.get('element').depth;
    const limit = 16;
    const presets = {
      straight: {leftEnd: 'lhf-manual', middles: ['element'], rightEnd: 'rhf-manual', cornerTurn: 1},
      corner: {leftEnd: 'lhf-power', middles: ['element', 'corner', 'element'], rightEnd: 'rhf-power', cornerTurn: 1},
      u: {leftEnd: 'lhf-manual', middles: ['element', 'corner', 'element', 'element', 'corner', 'element'], rightEnd: 'rhf-manual', cornerTurn: 1, rotation: 3},
      chaise: {leftEnd: 'lhf-chaise', middles: ['element'], rightEnd: 'rhf-power', cornerTurn: 1}
    };
    const copy = state => ({leftEnd: state.leftEnd, middles: [...state.middles], rightEnd: state.rightEnd, cornerTurn: state.cornerTurn, colour: state.colour ?? 'bone', rotation: state.rotation ?? 0, room: state.room ? {...state.room} : null});
    const sequence = state => [
      ...(state.leftEnd ? [{id: state.leftEnd, role: 'left', index: 0}] : []),
      ...state.middles.map((id, index) => ({id, role: 'middle', index})),
      ...(state.rightEnd ? [{id: state.rightEnd, role: 'right', index: 0}] : [])
    ];
    const geometry = state => {
      const items = [], joins = [];
      const vectors = [{x:1,y:0},{x:0,y:1},{x:-1,y:0},{x:0,y:-1}];
      let cursor = {x:0,y:0}, direction = 0;
      const entries = sequence(state);
      const facing = state.middles.includes('corner') ? state.cornerTurn : 1;
      entries.forEach((entry, order) => {
        const definition = moduleFor(entry.id,state.colour ?? 'bone');
        const vector = vectors[direction], normal = {x:-vector.y*facing,y:vector.x*facing};
        const start = {...cursor};
        let left, top, width, height, nextDirection = direction;
        if (entry.id === 'corner') {
          const side = definition.width;
          // Align the corner and projected chaises to the standard seat's back edge.
          const centre = {x:cursor.x + vector.x*side/2 + normal.x*(side-joinDepth)/2, y:cursor.y + vector.y*side/2 + normal.y*(side-joinDepth)/2};
          nextDirection = (direction + (state.cornerTurn === 1 ? 1 : 3)) % 4;
          const outgoing = vectors[nextDirection];
          cursor = {x:cursor.x+(vector.x+outgoing.x)*(side-joinDepth/2),y:cursor.y+(vector.y+outgoing.y)*(side-joinDepth/2)};
          left = centre.x-side/2; top = centre.y-side/2; width = side; height = side;
        } else {
          const along = definition.width, depth = definition.depth;
          const centre = {x:cursor.x + vector.x*along/2 + normal.x*(depth-joinDepth)/2, y:cursor.y + vector.y*along/2 + normal.y*(depth-joinDepth)/2};
          width = direction % 2 ? depth : along; height = direction % 2 ? along : depth;
          left = centre.x-width/2; top = centre.y-height/2;
          cursor = {x:cursor.x+vector.x*along,y:cursor.y+vector.y*along};
        }
        items.push({...entry,definition,order,direction,facing,left,top,width,height,start,end:{...cursor}});
        direction = nextDirection;
        if (order < entries.length-1) joins.push({...cursor,insertIndex:entry.role === 'left' ? 0 : entry.index+1,order});
      });
      const rotation = state.rotation ?? 0;
      const rotate = p => rotation===1?{x:-p.y,y:p.x}:rotation===2?{x:-p.x,y:-p.y}:rotation===3?{x:p.y,y:-p.x}:{...p};
      items.forEach(p=>{
        const a=rotate({x:p.left,y:p.top}),b=rotate({x:p.left+p.width,y:p.top+p.height});
        p.left=Math.min(a.x,b.x);p.top=Math.min(a.y,b.y);p.width=Math.abs(a.x-b.x);p.height=Math.abs(a.y-b.y);
        p.start=rotate(p.start);p.end=rotate(p.end);p.direction=(p.direction+rotation)%4;
      });
      joins.forEach(p=>Object.assign(p,rotate(p)));
      const bounds = items.length ? items.reduce((b,p)=>({minX:Math.min(b.minX,p.left),minY:Math.min(b.minY,p.top),maxX:Math.max(b.maxX,p.left+p.width),maxY:Math.max(b.maxY,p.top+p.height)}),{minX:Infinity,minY:Infinity,maxX:-Infinity,maxY:-Infinity}) : {minX:0,minY:0,maxX:0,maxY:0};
      const collisions = [];
      items.forEach((a,i)=>items.slice(i+1).forEach(b=>{
        if (Math.min(a.left+a.width,b.left+b.width)-Math.max(a.left,b.left)>0.01 && Math.min(a.top+a.height,b.top+b.height)-Math.max(a.top,b.top)>0.01) collisions.push([a.order,b.order]);
      }));
      return {items,joins,bounds,collisions,width:bounds.maxX-bounds.minX,depth:bounds.maxY-bounds.minY};
    };
    const validate = state => {
      if (!state || typeof state !== 'object' || !Array.isArray(state.middles) || ![1,-1].includes(state.cornerTurn)) return 'invalid';
      if (!colours.includes(state.colour ?? 'bone')) return 'invalid';
      if (![0,1,2,3].includes(state.rotation ?? 0)) return 'invalid';
      if (state.room != null && (!state.room || typeof state.room!=='object' || ![state.room.width,state.room.depth].every(n=>Number.isFinite(n)&&n>=100&&n<=2000))) return 'invalid';
      for (const [value,role] of [[state.leftEnd,'left'],[state.rightEnd,'right']]) {
        if (value !== null && (typeof value !== 'string' || definitions.get(value)?.role !== role)) return 'invalid';
      }
      if (state.middles.some(id=>typeof id !== 'string' || definitions.get(id)?.role !== 'middle')) return 'invalid';
      if (sequence(state).length>limit) return 'limit';
      if (state.middles.filter(id=>id==='corner').length>2) return 'corner_limit';
      if (geometry(state).collisions.length) return 'overlap';
      return null;
    };
    const candidate = (state,context,id) => {
      const next = copy(state);
      if (context.role === 'left') next.leftEnd = id;
      else if (context.role === 'right') next.rightEnd = id;
      else if (context.mode === 'replace') next.middles[context.index] = id;
      else next.middles.splice(context.index,0,id);
      return next;
    };
    const price = state => {
      const entries=sequence(state), pending=entries.filter(p=>!Number.isSafeInteger(definitions.get(p.id)?.priceEUR));
      const subtotal=entries.reduce((sum,p)=>sum+(definitions.get(p.id)?.priceEUR||0),0);
      return {subtotal,total:pending.length?null:subtotal,pending:pending.length};
    };
    const encode = state => ['D5',state.cornerTurn===1?'R':'L',state.leftEnd||'_',state.middles.join(',')||'_',state.rightEnd||'_',state.rotation??0,state.room?.width??'_',state.room?.depth??'_',state.colour??'bone'].join('|');
    const decode = code => {
      if (typeof code !== 'string' || code.length>600) return null;
      const [version,turn,left,middle,right,...extra] = code.split('|');
      if (!['D3','D4','D5'].includes(version) || !['R','L'].includes(turn) || !left || !middle || !right) return null;
      if(version==='D3'&&extra.length || version!=='D3'&&(extra.length!==(version==='D5'?4:3)||!/^[0-3]$/.test(extra[0]))) return null;
      const state = {leftEnd:left==='_'?null:left,middles:middle==='_'?[]:middle.split(','),rightEnd:right==='_'?null:right,cornerTurn:turn==='R'?1:-1,rotation:version!=='D3'?Number(extra[0]):0,room:null,colour:version==='D5'?extra[3]:'bone'};
      if(version!=='D3' && (extra[1]!=='_' || extra[2]!=='_')) {
        if(!extra.slice(1,3).every(n=>/^\d+(\.\d+)?$/.test(n))) return null;
        state.room={width:Number(extra[1]),depth:Number(extra[2])};
      }
      return validate(state) ? null : state;
    };
    return {definitions,moduleFor,colours,presets,limit,joinDepth,copy,sequence,geometry,validate,candidate,encode,decode,price};
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = {createModel};
  else scope.McGinleySofaModel = {createModel};
})(typeof globalThis !== 'undefined' ? globalThis : this);
